<template>
  <div class="pathfinding-container">
    <div class="container">
      <div class="control-panel">
        <h2>控制面板</h2>
        <div class="settings-section">
          <div class="setting-item">
            <label>棋盘大小：</label>
            <el-input-number 
              v-model="gridSize" 
              :min="MIN_GRID_SIZE" 
              :max="MAX_GRID_SIZE"
              :step="1"
              @change="handleGridSizeChange"
              size="small"
              style="width: 120px"
            ></el-input-number>
          </div>
          <div class="setting-item">
            <label>障碍物数量：</label>
            <el-input-number 
              v-model="wallCount" 
              :min="minWallCount" 
              :max="maxWallCount"
              :step="1"
              @change="handleWallCountChange"
              size="small"
              style="width: 120px"
            ></el-input-number>
          </div>
        </div>
        <el-select v-model="selectedAlgorithm" placeholder="请选择算法" style="width: 100%">
          <el-option value="astar" label="A*算法"></el-option>
          <el-option value="dijkstra" label="Dijkstra算法"></el-option>
          <el-option value="bfs" label="BFS算法"></el-option>
          <el-option value="dfs" label="DFS算法"></el-option>
          <el-option value="idastar" label="IDA*算法"></el-option>
          <el-option value="jps" label="JPS算法"></el-option>
          <el-option value="bibfs" label="双向BFS算法"></el-option>
          <el-option value="bellmanford" label="Bellman-Ford算法"></el-option>
        </el-select>
        <div class="button-grid">
          <el-button type="primary" @click="startPathfinding" :disabled="isRunning">开始</el-button>
          <el-button @click="handlePause" :disabled="!isRunning">{{ isPaused ? '继续' : '暂停' }}</el-button>
          <el-button @click="reset">重置</el-button>
          <el-button @click="randomizeStartEnd">随机起终点</el-button>
        </div>
        <div style="margin: 15px 0;">
          <label>速度：</label>
          <el-slider v-model="animationSpeed" :min="1" :max="100" :step="1"></el-slider>
        </div>
        <div class="status">
          <p>当前算法：<span>{{ selectedAlgorithm }}</span></p>
          <p>已探索节点：<span>{{ exploredNodes }}</span></p>
          <p>路径长度：<span>{{ pathLength }}</span></p>
          <p>执行时间：<span>{{ executionTime }}</span>ms</p>
          <p>内存使用：<span>{{ memoryUsage }}</span>KB</p>
        </div>

        <div class="algorithm-info" v-if="selectedAlgorithm">
          <h3>算法介绍</h3>
          <div class="info-content">
            <h4>{{ algorithmInfo[selectedAlgorithm].name }}</h4>
            <p class="description">{{ algorithmInfo[selectedAlgorithm].description }}</p>
            
            <div class="info-section">
              <h5>算法特性：</h5>
              <ul>
                <li v-for="(feature, index) in algorithmInfo[selectedAlgorithm].features" 
                    :key="'feature-' + index">{{ feature }}</li>
              </ul>
            </div>
            
            <div class="info-section">
              <h5>优点：</h5>
              <ul>
                <li v-for="(advantage, index) in algorithmInfo[selectedAlgorithm].advantages" 
                    :key="'advantage-' + index">{{ advantage }}</li>
              </ul>
            </div>
            
            <div class="info-section">
              <h5>缺点：</h5>
              <ul>
                <li v-for="(disadvantage, index) in algorithmInfo[selectedAlgorithm].disadvantages" 
                    :key="'disadvantage-' + index">{{ disadvantage }}</li>
              </ul>
            </div>
            
            <div class="info-section">
              <h5>适用场景：</h5>
              <ul>
                <li v-for="(scenario, index) in algorithmInfo[selectedAlgorithm].scenarios" 
                    :key="'scenario-' + index">{{ scenario }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div class="grid-container">
        <div class="grid">
          <div
            v-for="(row, i) in grid"
            :key="i"
            class="grid-row"
          >
            <div
              v-for="(cell, j) in row"
              :key="`${i}-${j}`"
              class="cell"
              :class="{
                'wall': cell === CELL_TYPES.WALL,
                'start': cell === CELL_TYPES.START,
                'end': cell === CELL_TYPES.END,
                'visited': cell === CELL_TYPES.VISITED,
                'path': cell === CELL_TYPES.PATH
              }"
              @click="handleCellClick(i, j)"
              @mousedown="handleMouseDown(i, j)"
              @mouseenter="handleMouseEnter(i, j)"
            ></div>
          </div>
        </div>
      </div>

      <div class="records-panel" :class="{ collapsed: isRecordsPanelCollapsed }">
        <h2 @click="toggleRecordsPanel">
          <span class="title-text">运行记录</span>
          <i v-if="!isRecordsPanelCollapsed" 
             class="el-icon-arrow-right toggle-icon" 
             :class="{ collapsed: isRecordsPanelCollapsed }">
          </i>
        </h2>
        <div class="records-content" v-show="!isRecordsPanelCollapsed">
          <div class="records-header">
            <el-button type="text" @click="clearRecords" :disabled="records.length === 0">
              清除记录
            </el-button>
          </div>
          <div class="records-table" v-if="records.length > 0">
            <el-table
              :data="records"
              size="small"
              style="width: 100%"
              :max-height="600"
              border
            >
              <el-table-column
                prop="id"
                label="序号"
                width="50"
                align="center"
                fixed
              ></el-table-column>
              <el-table-column
                prop="algorithm"
                label="算法"
                width="80"
                :formatter="row => {
                  const algorithms = {
                    'astar': 'A*',
                    'dijkstra': 'Dijkstra',
                    'bfs': 'BFS',
                    'dfs': 'DFS',
                    'idastar': 'IDA*',
                    'jps': 'JPS',
                    'bibfs': '双向BFS',
                    'bellmanford': 'B-F'
                  };
                  return algorithms[row.algorithm] || row.algorithm;
                }"
              ></el-table-column>
              <el-table-column
                prop="exploredNodes"
                label="探索节点"
                width="80"
                align="right"
              ></el-table-column>
              <el-table-column
                prop="pathLength"
                label="路径长度"
                width="80"
                align="right"
              ></el-table-column>
              <el-table-column
                prop="executionTime"
                label="耗时(ms)"
                width="80"
                align="right"
              ></el-table-column>
              <el-table-column
                prop="memoryUsage"
                label="内存(KB)"
                width="80"
                align="right"
              ></el-table-column>
            </el-table>
          </div>
          <div class="no-records" v-else>
            暂无记录
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_GRID_SIZE = 20;
const DEFAULT_WALL_COUNT = 150;
const MIN_GRID_SIZE = 10;
const MAX_GRID_SIZE = 50;
const MIN_WALL_RATIO = 0.1; // 最小障碍物比例 10%
const MAX_WALL_RATIO = 0.4; // 最大障碍物比例 40%

const CELL_TYPES = {
  EMPTY: 0,
  WALL: 1,
  START: 2,
  END: 3,
  VISITED: 4,
  PATH: 5
};

const STORAGE_KEY = 'pathfinding_state';

class PriorityQueue {
  constructor() {
    this.values = [];
  }
  
  enqueue(val, priority) {
    this.values.push({val, priority});
    this.sort();
  }
  
  dequeue() {
    return this.values.shift();
  }
  
  sort() {
    this.values.sort((a, b) => a.priority - b.priority);
  }
}

export default {
  name: 'PathFinding',
  data() {
    return {
      grid: [],
      startPos: null,
      endPos: null,
      isRunning: false,
      isPaused: false,
      selectedAlgorithm: 'astar',
      animationSpeed: 50,
      startTime: 0,
      executionTime: 0,
      exploredNodes: 0,
      pathLength: 0,
      CELL_TYPES,
      isDragging: false,
      dragType: null, // 'start' or 'end'
      gridSize: DEFAULT_GRID_SIZE,
      wallCount: DEFAULT_WALL_COUNT,
      records: [], // 用于存储算法运行记录
      memoryUsage: 0,  // 添加内存使用统计
      algorithmInfo: {
        astar: {
          name: 'A* 算法',
          description: '启发式搜索算法，结合了 Dijkstra 的最短路径和启发式估计',
          features: [
            '使用 f(n) = g(n) + h(n) 评估函数',
            '动态调整搜索方向',
            '保证找到最短路径'
          ],
          advantages: [
            '搜索效率高',
            '一定能找到最短路径',
            '空间复杂度适中'
          ],
          disadvantages: [
            '内存消耗较大',
            '启发函数的选择影响性能'
          ],
          scenarios: [
            '地图导航',
            '游戏寻路',
            '机器人路径规划'
          ]
        },
        dijkstra: {
          name: 'Dijkstra 算法',
          description: '经典的单源最短路径算法，基于广度优先搜索',
          features: [
            '按距离递增顺序扩展节点',
            '不使用启发式函数',
            '适用于带权图'
          ],
          advantages: [
            '保证最短路径',
            '实现简单',
            '适用性广'
          ],
          disadvantages: [
            '搜索范围大',
            '不考虑方向性',
            '效率低于 A*'
          ],
          scenarios: [
            '网络路由',
            '无权或等权图的最短路径',
            '基础路径规划'
          ]
        },
        bfs: {
          name: '广度优先搜索 (BFS)',
          description: '逐层扩展的搜索策略，保证在无权图中找到最短路径',
          features: [
            '按层次遍历',
            '使用队列存储待访问节点',
            '完整性保证'
          ],
          advantages: [
            '找到最短路径',
            '实现简单',
            '空间效率稳定'
          ],
          disadvantages: [
            '内存消耗大',
            '不考虑路径代价',
            '搜索范围大'
          ],
          scenarios: [
            '迷宫寻路',
            '无权图最短路径',
            '层次遍历问题'
          ]
        },
        dfs: {
          name: '深度优先搜索 (DFS)',
          description: '沿着一条路径一直探索到底的搜索策略',
          features: [
            '递归或栈实现',
            '回溯特性',
            '空间效率高'
          ],
          advantages: [
            '内存消耗小',
            '实现简单',
            '适合探索所有可能路径'
          ],
          disadvantages: [
            '不保证最短路径',
            '可能陷入死胡同',
            '搜索时间不可控'
          ],
          scenarios: [
            '迷宫生成',
            '路径可行性验证',
            '完整性探索'
          ]
        },
        idastar: {
          name: 'IDA* 算法',
          description: 'A* 的空间优化版本，结合了迭代加深和启发式搜索',
          features: [
            '迭代加深的 f 值限制',
            '深度优先的搜索方式',
            '启发式评估'
          ],
          advantages: [
            '空间复杂度低 O(d)',
            '保证最优解',
            '适合大规模问题'
          ],
          disadvantages: [
            '重复搜索多',
            '收敛速度慢',
            '不适合网格结构'
          ],
          scenarios: [
            '内存受限环境',
            '大规模路径规划',
            '状态空间搜索'
          ]
        },
        jps: {
          name: 'Jump Point Search',
          description: 'A* 的优化版本，通过跳点降低搜索空间',
          features: [
            '识别对称路径',
            '跳点优化',
            '强制邻居检查'
          ],
          advantages: [
            '搜索效率高',
            '节点扩展少',
            '保证最短路径'
          ],
          disadvantages: [
            '仅适用于网格地图',
            '实现复杂',
            '对地图结构敏感'
          ],
          scenarios: [
            '游戏寻路优化',
            '网格地图导航',
            '实时路径规划'
          ]
        },
        bibfs: {
          name: '双向广度优先搜索',
          description: '同时从起点和终点开始搜索，直到两个搜索相遇',
          features: [
            '双向扩展',
            '早期终止',
            '搜索空间对半分'
          ],
          advantages: [
            '搜索范围小',
            '收敛速度快',
            '保证最短路径'
          ],
          disadvantages: [
            '实现复杂',
            '内存消耗大',
            '需要额外的相遇检查'
          ],
          scenarios: [
            '已知起点和终点的搜索',
            '中等规模地图',
            '对称性强的场景'
          ]
        },
        bellmanford: {
          name: 'Bellman-Ford 算法',
          description: '可处理负权边的单源最短路径算法',
          features: [
            '支持负权边',
            '检测负权环',
            '动态规划特性'
          ],
          advantages: [
            '功能全面',
            '适用性广',
            '实现简单'
          ],
          disadvantages: [
            '时间复杂度高',
            '收敛速度慢',
            '不适合大规模图'
          ],
          scenarios: [
            '网络路由',
            '货币兑换',
            '负权图最短路径'
          ]
        }
      },
      isRecordsPanelCollapsed: false,
    }
  },
  computed: {
    maxWallCount() {
      return Math.floor(this.gridSize * this.gridSize * MAX_WALL_RATIO);
    },
    minWallCount() {
      return Math.floor(this.gridSize * this.gridSize * MIN_WALL_RATIO);
    }
  },
  created() {
    this.initializeComponent();
  },
  mounted() {
    window.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.handleMouseUp);
    // 组件销毁时清除本地存储
    localStorage.removeItem(STORAGE_KEY);
  },
  activated() {
    // 当组件被激活（重新显示）时重置状态
    this.initializeComponent();
  },
  deactivated() {
    // 当组件被停用（隐藏）时清理状态
    this.cleanupComponent();
  },
  methods: {
    initializeComponent() {
      // 重置所有状态到初始值
      this.gridSize = DEFAULT_GRID_SIZE;
      this.wallCount = DEFAULT_WALL_COUNT;
      this.selectedAlgorithm = 'astar';
      this.animationSpeed = 50;
      this.isRunning = false;
      this.isPaused = false;
      this.startTime = 0;
      this.executionTime = 0;
      this.exploredNodes = 0;
      this.pathLength = 0;
      this.isDragging = false;
      this.dragType = null;
      
      // 初始化网格
      this.initGrid();
      this.generateWalls();
      
      // 清除本地存储
      localStorage.removeItem(STORAGE_KEY);
    },

    cleanupComponent() {
      // 停止所有正在进行的操作
      this.isRunning = false;
      this.isPaused = true;
      this.isDragging = false;
      
      // 清除本地存储
      localStorage.removeItem(STORAGE_KEY);
    },

    loadState() {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const state = JSON.parse(savedState);
        this.grid = state.grid;
        this.startPos = state.startPos;
        this.endPos = state.endPos;
      } else {
        this.initGrid();
        this.generateWalls();
      }
    },

    saveState() {
      const state = {
        grid: this.grid,
        startPos: this.startPos,
        endPos: this.endPos
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    },

    initGrid() {
      this.grid = Array(this.gridSize).fill().map(() => Array(this.gridSize).fill(CELL_TYPES.EMPTY));
    },

    generateWalls() {
      let wallsPlaced = 0;
      while (wallsPlaced < this.wallCount) {
        const x = Math.floor(Math.random() * this.gridSize);
        const y = Math.floor(Math.random() * this.gridSize);
        if (this.grid[x][y] === CELL_TYPES.EMPTY) {
          this.grid[x][y] = CELL_TYPES.WALL;
          wallsPlaced++;
        }
      }
      this.saveState();
    },

    randomizeStartEnd() {
      // 清除现有的起点和终点
      if (this.startPos) {
        this.$set(this.grid[this.startPos.x], this.startPos.y, CELL_TYPES.EMPTY);
      }
      if (this.endPos) {
        this.$set(this.grid[this.endPos.x], this.endPos.y, CELL_TYPES.EMPTY);
      }

      // 获取所有空格子的位置
      const emptyCells = [];
      for (let i = 0; i < this.gridSize; i++) {
        for (let j = 0; j < this.gridSize; j++) {
          if (this.grid[i][j] === CELL_TYPES.EMPTY) {
            emptyCells.push({x: i, y: j});
          }
        }
      }

      // 随机选择两个不同的空格子作为起点和终点
      if (emptyCells.length >= 2) {
        // 随机选择起点
        const startIndex = Math.floor(Math.random() * emptyCells.length);
        const start = emptyCells[startIndex];
        this.startPos = {x: start.x, y: start.y};
        this.$set(this.grid[start.x], start.y, CELL_TYPES.START);
        
        // 从剩余格子中随机选择终点
        emptyCells.splice(startIndex, 1);
        const endIndex = Math.floor(Math.random() * emptyCells.length);
        const end = emptyCells[endIndex];
        this.endPos = {x: end.x, y: end.y};
        this.$set(this.grid[end.x], end.y, CELL_TYPES.END);
      } else {
        // 如果没有足够的空格子，提示用户
        this.$message.warning('没有足够的空格子来设置起点和终点！');
      }
      
      this.saveState();
      this.clearRecords();
    },

    handleCellClick(i, j) {
      if (this.isRunning) return;
      
      const currentCell = this.grid[i][j];
      
      // 如果点击的是起点或终点，先清除它
      if (currentCell === CELL_TYPES.START) {
        this.$set(this.grid[i], j, CELL_TYPES.EMPTY);
        this.startPos = null;
        this.saveState();
        return;
      }
      if (currentCell === CELL_TYPES.END) {
        this.$set(this.grid[i], j, CELL_TYPES.EMPTY);
        this.endPos = null;
        this.saveState();
        return;
      }
      
      // 设置起点
      if (!this.startPos && currentCell !== CELL_TYPES.END) {
        this.startPos = {x: i, y: j};
        this.$set(this.grid[i], j, CELL_TYPES.START);
        this.saveState();
        return;
      }
      
      // 设置终点
      if (!this.endPos && currentCell !== CELL_TYPES.START) {
        this.endPos = {x: i, y: j};
        this.$set(this.grid[i], j, CELL_TYPES.END);
        this.saveState();
        return;
      }
      
      // 切换障碍物
      this.$set(this.grid[i], j, currentCell === CELL_TYPES.WALL ? CELL_TYPES.EMPTY : CELL_TYPES.WALL);
      this.saveState();
    },

    handleMouseDown(i, j) {
      if (this.isRunning) return;
      
      if (this.grid[i][j] === CELL_TYPES.START) {
        this.isDragging = true;
        this.dragType = 'start';
      } else if (this.grid[i][j] === CELL_TYPES.END) {
        this.isDragging = true;
        this.dragType = 'end';
      }
    },

    handleMouseEnter(i, j) {
      if (!this.isDragging || this.isRunning) return;

      const oldPos = this.dragType === 'start' ? this.startPos : this.endPos;
      if (oldPos && this.grid[i][j] === CELL_TYPES.EMPTY) {
        // 清除旧位置
        this.$set(this.grid[oldPos.x], oldPos.y, CELL_TYPES.EMPTY);
        
        // 设置新位置
        this.$set(this.grid[i], j, this.dragType === 'start' ? CELL_TYPES.START : CELL_TYPES.END);
        if (this.dragType === 'start') {
          this.startPos = {x: i, y: j};
        } else {
          this.endPos = {x: i, y: j};
        }
      }
    },

    handleMouseUp() {
      if (this.isDragging) {
        this.isDragging = false;
        this.dragType = null;
        this.saveState();
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, Math.max(10, 110 - this.animationSpeed)));
    },

    heuristic(a, b) {
      return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    },

    async updateCellStatus(x, y, type) {
      if (this.grid[x][y] !== CELL_TYPES.END && this.grid[x][y] !== CELL_TYPES.START) {
        this.$set(this.grid[x], y, type);
        if (type === CELL_TYPES.VISITED) {
          this.exploredNodes++;
        }
        if (type === CELL_TYPES.PATH) {
          this.pathLength++;
        }
      }
    },

    async reconstructPath(cameFrom, current) {
      const path = [];
      while (cameFrom.has(current)) {
        const [x, y] = current.split(',').map(Number);
        if (this.grid[x][y] !== CELL_TYPES.END && this.grid[x][y] !== CELL_TYPES.START) {
          await this.updateCellStatus(x, y, CELL_TYPES.PATH);
          await this.sleep(Math.max(10, 110 - this.animationSpeed));
        }
        current = cameFrom.get(current);
        path.unshift(current);
      }
      return path;
    },

    async idastar() {
      // 数据结构
      const visited = new Set();
      const transpositionTable = new Map();
      const path = [];
      let nodesExplored = 0;
      
      // 计算 f 值
      const calculateF = (x, y, g) => {
        return g + this.heuristic({x, y}, this.endPos);
      };
      
      // 检查坐标是否有效
      const isValid = (x, y) => {
        return x >= 0 && x < this.gridSize && 
               y >= 0 && y < this.gridSize && 
               this.grid[x][y] !== CELL_TYPES.WALL;
      };

      // 检查是否为拐角
      const isCorner = (x, y) => {
        let wallCount = 0;
        const neighbors = [
          [x-1, y], [x+1, y],
          [x, y-1], [x, y+1]
        ];
        
        for (const [nx, ny] of neighbors) {
          if (!isValid(nx, ny) || this.grid[nx][ny] === CELL_TYPES.WALL) {
            wallCount++;
          }
        }
        return wallCount >= 3;
      };
      
      // 获取相邻节点
      const getNeighbors = (x, y, parentX, parentY) => {
        const neighbors = [];
        const directions = [
          [0, 1], [1, 0], [0, -1], [-1, 0],
          [1, 1], [1, -1], [-1, 1], [-1, -1]
        ];
        
        // 根据到目标的距离对方向进行排序
        directions.sort((a, b) => {
          const distA = Math.abs((x + a[0]) - this.endPos.x) + Math.abs((y + a[1]) - this.endPos.y);
          const distB = Math.abs((x + b[0]) - this.endPos.x) + Math.abs((y + b[1]) - this.endPos.y);
          return distA - distB;
        });
        
        for (const [dx, dy] of directions) {
          const newX = x + dx;
          const newY = y + dy;
          
          // 剪枝1: 基本有效性检查
          if (!isValid(newX, newY)) continue;
          
          // 剪枝2: 避免回头
          if (parentX !== undefined && parentY !== undefined) {
            if (newX === parentX && newY === parentY) continue;
          }
          
          // 剪枝3: 对角线移动检查
          if (Math.abs(dx) === 1 && Math.abs(dy) === 1) {
            if (!isValid(x + dx, y) || !isValid(x, y + dy)) continue;
          }
          
          // 剪枝4: 拐角检查
          if (isCorner(newX, newY)) continue;
          
          // 剪枝5: 方向惯性
          if (parentX !== undefined && parentY !== undefined) {
            const currentDx = x - parentX;
            const currentDy = y - parentY;
            // 如果当前移动方向与父节点移动方向相反，增加代价
            if (dx === -currentDx && dy === -currentDy) continue;
          }
          
          neighbors.push([newX, newY]);
        }
        return neighbors;
      };
      
      // DFS 搜索函数
      const search = async (x, y, g, bound, parentX, parentY) => {
        const f = calculateF(x, y, g);
        const node = `${x},${y}`;
        
        // 计算内存使用
        this.calculateMemoryUsage([visited, transpositionTable, path]);
        
        // 剪枝6: 状态缓存检查
        if (transpositionTable.has(node)) {
          const cachedF = transpositionTable.get(node);
          if (cachedF <= f) {
            return cachedF;
          }
        }
        
        // 剪枝7: 边界检查
        if (f > bound) {
          transpositionTable.set(node, f);
          return f;
        }
        
        // 找到目标
        if (x === this.endPos.x && y === this.endPos.y) {
          return true;
        }
        
        // 剪枝8: 启发式剪枝
        const remainingDist = this.heuristic({x, y}, this.endPos);
        if (g + remainingDist > bound) {
          return g + remainingDist;
        }
        
        // 标记当前节点为已访问
        if (!visited.has(node)) {
          visited.add(node);
          nodesExplored++;
          path.push([x, y]);
          
          // 更新显示
          if (this.grid[x][y] !== CELL_TYPES.START && this.grid[x][y] !== CELL_TYPES.END) {
            await this.updateCellStatus(x, y, CELL_TYPES.VISITED);
            await this.sleep(Math.max(10, 110 - this.animationSpeed));
          }
        }
        
        // 获取并探索相邻节点
        let minBound = Infinity;
        const neighbors = getNeighbors(x, y, parentX, parentY);
        
        for (const [newX, newY] of neighbors) {
          const newNode = `${newX},${newY}`;
          if (visited.has(newNode)) continue;
          
          const result = await search(newX, newY, g + 1, bound, x, y);
          
          // 找到路径
          if (result === true) {
            return true;
          }
          
          // 更新最小界限值
          if (typeof result === 'number') {
            minBound = Math.min(minBound, result);
          }
          
          // 检查是否暂停
          if (this.isPaused) {
            return minBound;
          }
        }
        
        // 回溯，移除当前节点
        const lastNode = path.pop();
        if (lastNode) {
          visited.delete(`${lastNode[0]},${lastNode[1]}`);
        }
        
        // 缓存当前状态的 f 值
        transpositionTable.set(node, minBound);
        return minBound;
      };
      
      // 迭代加深搜索主循环
      let bound = this.heuristic(this.startPos, this.endPos);
      const maxBound = this.gridSize * this.gridSize * 2;
      let prevBound = bound;
      
      while (bound < maxBound && !this.isPaused) {
        path.length = 0;
        visited.clear();
        nodesExplored = 0;
        
        const result = await search(this.startPos.x, this.startPos.y, 0, bound);
        
        if (result === true) {
          for (const [x, y] of path) {
            if (this.grid[x][y] !== CELL_TYPES.START && this.grid[x][y] !== CELL_TYPES.END) {
              await this.updateCellStatus(x, y, CELL_TYPES.PATH);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
          }
          return true;
        }
        
        if (result === Infinity || bound >= maxBound) {
          return false;
        }
        
        const boundIncrease = Math.max(1, Math.floor((result - bound) / 2));
        bound += boundIncrease;
        
        if (bound <= prevBound) {
          bound = prevBound + 1;
        }
        prevBound = bound;
      }
      
      return false;
    },

    async astar() {
      const openSet = new PriorityQueue();
      const closedSet = new Set();
      const cameFrom = new Map();
      const gScore = new Map();
      const fScore = new Map();
      
      const start = `${this.startPos.x},${this.startPos.y}`;
      openSet.enqueue(start, 0);
      gScore.set(start, 0);
      fScore.set(start, this.heuristic(this.startPos, this.endPos));
      
      while (openSet.values.length > 0 && !this.isPaused) {
        // 计算内存使用
        this.calculateMemoryUsage([
          openSet.values,
          closedSet,
          cameFrom,
          gScore,
          fScore
        ]);
        
        const current = openSet.dequeue().val;
        const [x, y] = current.split(',').map(Number);
        
        if (x === this.endPos.x && y === this.endPos.y) {
          await this.reconstructPath(cameFrom, current);
          return true;
        }
        
        closedSet.add(current);
        
        for (const [dx, dy] of [[0, 1], [1, 0], [0, -1], [-1, 0]]) {
          const newX = x + dx;
          const newY = y + dy;
          
          if (newX < 0 || newX >= this.gridSize || newY < 0 || newY >= this.gridSize) continue;
          if (this.grid[newX][newY] === CELL_TYPES.WALL) continue;
          
          const neighbor = `${newX},${newY}`;
          if (closedSet.has(neighbor)) continue;
          
          const tentativeGScore = gScore.get(current) + 1;
          
          if (!gScore.has(neighbor) || tentativeGScore < gScore.get(neighbor)) {
            cameFrom.set(neighbor, current);
            gScore.set(neighbor, tentativeGScore);
            const f = tentativeGScore + this.heuristic({x: newX, y: newY}, this.endPos);
            fScore.set(neighbor, f);
            openSet.enqueue(neighbor, f);
            
            if (this.grid[newX][newY] !== CELL_TYPES.END) {
              await this.updateCellStatus(newX, newY, CELL_TYPES.VISITED);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
          }
        }
      }
      return false;
    },

    async dijkstra() {
      const distances = new Map();
      const cameFrom = new Map();
      const visited = new Set();
      const queue = new PriorityQueue();
      
      // 初始化距离
      const start = `${this.startPos.x},${this.startPos.y}`;
      distances.set(start, 0);
      queue.enqueue(start, 0);
      
      while (queue.values.length > 0 && !this.isPaused) {
        // 计算内存使用
        this.calculateMemoryUsage([
          queue.values,
          visited,
          distances,
          cameFrom
        ]);
        
        const current = queue.dequeue().val;
        const [x, y] = current.split(',').map(Number);
        
        if (visited.has(current)) continue;
        visited.add(current);
        
        if (x === this.endPos.x && y === this.endPos.y) {
          await this.reconstructPath(cameFrom, current);
          return true;
        }
        
        for (const [dx, dy] of [[0, 1], [1, 0], [0, -1], [-1, 0]]) {
          const newX = x + dx;
          const newY = y + dy;
          
          if (newX < 0 || newX >= this.gridSize || newY < 0 || newY >= this.gridSize) continue;
          if (this.grid[newX][newY] === CELL_TYPES.WALL) continue;
          
          const neighbor = `${newX},${newY}`;
          const newDist = distances.get(current) + 1;
          
          if (!distances.has(neighbor) || newDist < distances.get(neighbor)) {
            distances.set(neighbor, newDist);
            cameFrom.set(neighbor, current);
            queue.enqueue(neighbor, newDist);
            
            if (this.grid[newX][newY] !== CELL_TYPES.END) {
              await this.updateCellStatus(newX, newY, CELL_TYPES.VISITED);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
          }
        }
      }
      return false;
    },

    async bfs() {
      const queue = [];
      const visited = new Set();
      const cameFrom = new Map();
      
      const start = `${this.startPos.x},${this.startPos.y}`;
      queue.push(start);
      visited.add(start);
      
      while (queue.length > 0 && !this.isPaused) {
        // 计算内存使用
        this.calculateMemoryUsage([
          queue,
          visited,
          cameFrom
        ]);
        
        const current = queue.shift();
        const [x, y] = current.split(',').map(Number);
        
        if (x === this.endPos.x && y === this.endPos.y) {
          await this.reconstructPath(cameFrom, current);
          return true;
        }
        
        for (const [dx, dy] of [[0, 1], [1, 0], [0, -1], [-1, 0]]) {
          const newX = x + dx;
          const newY = y + dy;
          
          if (newX < 0 || newX >= this.gridSize || newY < 0 || newY >= this.gridSize) continue;
          if (this.grid[newX][newY] === CELL_TYPES.WALL) continue;
          
          const neighbor = `${newX},${newY}`;
          if (!visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push(neighbor);
            cameFrom.set(neighbor, current);
            
            if (this.grid[newX][newY] !== CELL_TYPES.END) {
              await this.updateCellStatus(newX, newY, CELL_TYPES.VISITED);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
          }
        }
      }
      return false;
    },

    async dfs() {
      const stack = [];
      const visited = new Set();
      const cameFrom = new Map();
      
      const start = `${this.startPos.x},${this.startPos.y}`;
      stack.push(start);
      
      // 计算到目标的曼哈顿距离
      const getDistance = (x, y) => {
        return Math.abs(x - this.endPos.x) + Math.abs(y - this.endPos.y);
      };
      
      while (stack.length > 0 && !this.isPaused) {
        // 计算内存使用
        this.calculateMemoryUsage([
          stack,
          visited,
          cameFrom
        ]);
        
        const current = stack.pop();
        const [x, y] = current.split(',').map(Number);
        
        if (visited.has(current)) continue;
        visited.add(current);
        
        if (x === this.endPos.x && y === this.endPos.y) {
          await this.reconstructPath(cameFrom, current);
          return true;
        }
        
        if (this.grid[x][y] !== CELL_TYPES.START && this.grid[x][y] !== CELL_TYPES.END) {
          await this.updateCellStatus(x, y, CELL_TYPES.VISITED);
          await this.sleep(Math.max(10, 110 - this.animationSpeed));
        }
        
        // 获取并排序相邻节点，优先选择离目标近的方向
        const neighbors = [];
        for (const [dx, dy] of [[0, 1], [1, 0], [0, -1], [-1, 0]]) {
          const newX = x + dx;
          const newY = y + dy;
          
          if (newX < 0 || newX >= this.gridSize || newY < 0 || newY >= this.gridSize) continue;
          if (this.grid[newX][newY] === CELL_TYPES.WALL) continue;
          
          const neighbor = `${newX},${newY}`;
          if (!visited.has(neighbor)) {
            neighbors.push({
              pos: neighbor,
              distance: getDistance(newX, newY)
            });
          }
        }
        
        // 按到目标的距离排序，距离近的后压入栈（这样会先弹出）
        neighbors.sort((a, b) => b.distance - a.distance);
        for (const {pos} of neighbors) {
          stack.push(pos);
          if (!cameFrom.has(pos)) {
            cameFrom.set(pos, current);
          }
        }
      }
      return false;
    },

    async jps() {
      const openSet = new PriorityQueue();
      const closedSet = new Set();
      const cameFrom = new Map();
      const gScore = new Map();
      const fScore = new Map();
      
      const start = `${this.startPos.x},${this.startPos.y}`;
      openSet.enqueue(start, 0);
      gScore.set(start, 0);
      fScore.set(start, this.heuristic(this.startPos, this.endPos));

      // 检查坐标是否有效
      const isValid = (x, y) => {
        return x >= 0 && x < this.gridSize && y >= 0 && y < this.gridSize && this.grid[x][y] !== CELL_TYPES.WALL;
      };

      // 标记访问状态
      const markVisited = async (x, y) => {
        if (this.grid[x][y] !== CELL_TYPES.START && this.grid[x][y] !== CELL_TYPES.END) {
          await this.updateCellStatus(x, y, CELL_TYPES.VISITED);
          await this.sleep(Math.max(10, 110 - this.animationSpeed));
        }
      };

      // 跳点搜索函数
      const jump = async (x, y, dx, dy) => {
        const newX = x + dx;
        const newY = y + dy;
        
        if (!isValid(newX, newY)) return null;
        await markVisited(newX, newY);
        
        // 如果到达终点或终点相邻，直接返回
        if (newX === this.endPos.x && newY === this.endPos.y ||
            Math.abs(newX - this.endPos.x) + Math.abs(newY - this.endPos.y) === 1) {
          return [newX, newY];
        }

        // 对角线移动
        if (dx !== 0 && dy !== 0) {
          // 检查水平和垂直方向
          if (await jump(newX, newY, dx, 0) || await jump(newX, newY, 0, dy)) {
            return [newX, newY];
          }
          
          // 检查强制邻居
          if (!isValid(x, y + dy) && isValid(newX, y + dy) ||
              !isValid(x + dx, y) && isValid(x + dx, newY)) {
            return [newX, newY];
          }
        } else {
          // 直线移动时检查强制邻居
          if (dx !== 0) { // 水平移动
            if ((!isValid(x, y + 1) && isValid(newX, y + 1)) ||
                (!isValid(x, y - 1) && isValid(newX, y - 1))) {
              return [newX, newY];
            }
          } else { // 垂直移动
            if ((!isValid(x + 1, y) && isValid(x + 1, newY)) ||
                (!isValid(x - 1, y) && isValid(x - 1, newY))) {
              return [newX, newY];
            }
          }
        }

        // 继续在当前方向搜索
        if (isValid(newX + dx, newY + dy)) {
          const nextJump = await jump(newX, newY, dx, dy);
          if (nextJump) return [newX, newY];
        }

        return null;
      };

      // 获取邻居节点
      const getNeighbors = async (node) => {
        const [x, y] = node.split(',').map(Number);
        const neighbors = [];
        const dirs = [];
        
        // 获取搜索方向
        if (node === start) {
          // 起点：所有8个方向
          dirs.push(...[
            [-1, -1], [-1, 0], [-1, 1],
            [0, -1],          [0, 1],
            [1, -1],  [1, 0], [1, 1]
          ]);
        } else {
          const parent = cameFrom.get(node);
          const [px, py] = parent.split(',').map(Number);
          const dx = Math.sign(x - px);
          const dy = Math.sign(y - py);
          
          if (dx !== 0 && dy !== 0) {
            // 对角线移动：保持对角线方向和两个基本方向
            dirs.push(
              [dx, dy],  // 对角线
              [dx, 0],   // 水平
              [0, dy]    // 垂直
            );
            // 检查是否需要额外的对角线方向
            if (!isValid(x - dx, y) || !isValid(x, y - dy)) {
              dirs.push([dx, -dy], [-dx, dy]);
            }
          } else {
            // 直线移动：保持当前方向
            dirs.push([dx, dy]);
            // 检查两侧的对角线方向
            if (dx === 0) {
              if (!isValid(x + 1, y)) dirs.push([1, dy]);
              if (!isValid(x - 1, y)) dirs.push([-1, dy]);
            } else {
              if (!isValid(x, y + 1)) dirs.push([dx, 1]);
              if (!isValid(x, y - 1)) dirs.push([dx, -1]);
            }
          }
        }
        
        // 搜索每个方向的跳点
        for (const [dx, dy] of dirs) {
          const jumpPoint = await jump(x, y, dx, dy);
          if (jumpPoint) {
            neighbors.push(`${jumpPoint[0]},${jumpPoint[1]}`);
          }
        }
        
        return neighbors;
      };
      
      // 主循环
      while (openSet.values.length > 0 && !this.isPaused) {
        this.calculateMemoryUsage([openSet.values, closedSet, cameFrom, gScore, fScore]);
        
        const current = openSet.dequeue().val;
        const [x, y] = current.split(',').map(Number);
        
        if (x === this.endPos.x && y === this.endPos.y) {
          await this.reconstructPath(cameFrom, current);
          return true;
        }
        
        closedSet.add(current);
        
        const neighbors = await getNeighbors(current);
        for (const neighbor of neighbors) {
          if (closedSet.has(neighbor)) continue;
          
          const [nx, ny] = neighbor.split(',').map(Number);
          const tentativeGScore = gScore.get(current) + Math.sqrt((nx - x) ** 2 + (ny - y) ** 2);
          
          if (!gScore.has(neighbor) || tentativeGScore < gScore.get(neighbor)) {
            cameFrom.set(neighbor, current);
            gScore.set(neighbor, tentativeGScore);
            const f = tentativeGScore + this.heuristic({x: nx, y: ny}, this.endPos);
            fScore.set(neighbor, f);
            openSet.enqueue(neighbor, f);
          }
        }
      }
      
      return false;
    },

    // 双向 BFS 算法实现
    async bibfs() {
      const startQueue = [];
      const endQueue = [];
      const startVisited = new Set();
      const endVisited = new Set();
      const startCameFrom = new Map();
      const endCameFrom = new Map();
      
      const start = `${this.startPos.x},${this.startPos.y}`;
      const end = `${this.endPos.x},${this.endPos.y}`;
      
      startQueue.push(start);
      endQueue.push(end);
      startVisited.add(start);
      endVisited.add(end);
      
      const findIntersection = () => {
        for (const node of startVisited) {
          if (endVisited.has(node)) {
            return node;
          }
        }
        return null;
      };
      
      while (startQueue.length > 0 && endQueue.length > 0 && !this.isPaused) {
        // 计算内存使用
        this.calculateMemoryUsage([
          startQueue,
          endQueue,
          startVisited,
          endVisited,
          startCameFrom,
          endCameFrom
        ]);
        
        // 从起点扩展
        const currentStart = startQueue.shift();
        const [sx, sy] = currentStart.split(',').map(Number);
        
        for (const [dx, dy] of [[0, 1], [1, 0], [0, -1], [-1, 0]]) {
          const newX = sx + dx;
          const newY = sy + dy;
          
          if (newX < 0 || newX >= this.gridSize || newY < 0 || newY >= this.gridSize) continue;
          if (this.grid[newX][newY] === CELL_TYPES.WALL) continue;
          
          const neighbor = `${newX},${newY}`;
          if (!startVisited.has(neighbor)) {
            startQueue.push(neighbor);
            startVisited.add(neighbor);
            startCameFrom.set(neighbor, currentStart);
            
            if (this.grid[newX][newY] !== CELL_TYPES.END) {
              await this.updateCellStatus(newX, newY, CELL_TYPES.VISITED);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
          }
        }
        
        // 从终点扩展
        const currentEnd = endQueue.shift();
        const [ex, ey] = currentEnd.split(',').map(Number);
        
        for (const [dx, dy] of [[0, 1], [1, 0], [0, -1], [-1, 0]]) {
          const newX = ex + dx;
          const newY = ey + dy;
          
          if (newX < 0 || newX >= this.gridSize || newY < 0 || newY >= this.gridSize) continue;
          if (this.grid[newX][newY] === CELL_TYPES.WALL) continue;
          
          const neighbor = `${newX},${newY}`;
          if (!endVisited.has(neighbor)) {
            endQueue.push(neighbor);
            endVisited.add(neighbor);
            endCameFrom.set(neighbor, currentEnd);
            
            if (this.grid[newX][newY] !== CELL_TYPES.START) {
              await this.updateCellStatus(newX, newY, CELL_TYPES.VISITED);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
          }
        }
        
        // 检查是否相遇
        const intersection = findIntersection();
        if (intersection) {
          // 从起点到相遇点的路径
          let current = intersection;
          while (current !== start) {
            const [x, y] = current.split(',').map(Number);
            if (this.grid[x][y] !== CELL_TYPES.START && this.grid[x][y] !== CELL_TYPES.END) {
              await this.updateCellStatus(x, y, CELL_TYPES.PATH);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
            current = startCameFrom.get(current);
          }
          
          // 从相遇点到终点的路径
          current = endCameFrom.get(intersection);
          while (current !== end) {
            const [x, y] = current.split(',').map(Number);
            if (this.grid[x][y] !== CELL_TYPES.START && this.grid[x][y] !== CELL_TYPES.END) {
              await this.updateCellStatus(x, y, CELL_TYPES.PATH);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
            current = endCameFrom.get(current);
          }
          
          return true;
        }
      }
      return false;
    },

    // Bellman-Ford 算法实现
    async bellmanford() {
      const distances = new Map();
      const cameFrom = new Map();
      const nodes = [];
      const edges = [];
      
      // 收集所有节点和边
      for (let i = 0; i < this.gridSize; i++) {
        for (let j = 0; j < this.gridSize; j++) {
          if (this.grid[i][j] !== CELL_TYPES.WALL) {
            const node = `${i},${j}`;
            nodes.push(node);
            
            // 添加到相邻节点的边
            for (const [dx, dy] of [[0, 1], [1, 0]]) {
              const newX = i + dx;
              const newY = j + dy;
              
              if (newX < this.gridSize && newY < this.gridSize && this.grid[newX][newY] !== CELL_TYPES.WALL) {
                edges.push({
                  from: node,
                  to: `${newX},${newY}`,
                  weight: 1
                });
                edges.push({
                  from: `${newX},${newY}`,
                  to: node,
                  weight: 1
                });
              }
            }
          }
        }
      }
      
      // 初始化距离
      for (const node of nodes) {
        distances.set(node, Infinity);
      }
      const start = `${this.startPos.x},${this.startPos.y}`;
      distances.set(start, 0);
      
      // Bellman-Ford 主循环
      for (let i = 0; i < nodes.length - 1 && !this.isPaused; i++) {
        // 计算内存使用
        this.calculateMemoryUsage([
          distances,
          cameFrom,
          nodes,
          edges
        ]);
        
        let updated = false;
        for (const edge of edges) {
          const newDist = distances.get(edge.from) + edge.weight;
          if (newDist < distances.get(edge.to)) {
            distances.set(edge.to, newDist);
            cameFrom.set(edge.to, edge.from);
            updated = true;
            
            const [x, y] = edge.to.split(',').map(Number);
            if (this.grid[x][y] !== CELL_TYPES.START && this.grid[x][y] !== CELL_TYPES.END) {
              await this.updateCellStatus(x, y, CELL_TYPES.VISITED);
              await this.sleep(Math.max(10, 110 - this.animationSpeed));
            }
          }
        }
        if (!updated) break;
      }
      
      // 检查是否找到路径
      const end = `${this.endPos.x},${this.endPos.y}`;
      if (distances.get(end) === Infinity) return false;
      
      // 重建路径
      let current = end;
      while (current !== start) {
        const [x, y] = current.split(',').map(Number);
        if (this.grid[x][y] !== CELL_TYPES.START && this.grid[x][y] !== CELL_TYPES.END) {
          await this.updateCellStatus(x, y, CELL_TYPES.PATH);
          await this.sleep(Math.max(10, 110 - this.animationSpeed));
        }
        current = cameFrom.get(current);
      }
      
      return true;
    },

    async startPathfinding() {
      if (!this.startPos || !this.endPos) {
        this.$message.warning('请先设置起点和终点！');
        return;
      }
      
      if (this.isRunning) return;
      
      // 重置状态
      this.isRunning = true;
      this.isPaused = false;
      this.startTime = Date.now();
      this.exploredNodes = 0;
      this.pathLength = 0;
      this.memoryUsage = 0;  // 重置内存使用
      
      // 清除之前的访问路径
      for (let i = 0; i < this.gridSize; i++) {
        for (let j = 0; j < this.gridSize; j++) {
          if (this.grid[i][j] === CELL_TYPES.VISITED || this.grid[i][j] === CELL_TYPES.PATH) {
            this.grid[i][j] = CELL_TYPES.EMPTY;
          }
        }
      }
      
      let success = false;
      
      switch (this.selectedAlgorithm) {
        case 'astar':
          success = await this.astar();
          break;
        case 'dijkstra':
          success = await this.dijkstra();
          break;
        case 'bfs':
          success = await this.bfs();
          break;
        case 'dfs':
          success = await this.dfs();
          break;
        case 'idastar':
          success = await this.idastar();
          break;
        case 'jps':
          success = await this.jps();
          break;
        case 'bibfs':
          success = await this.bibfs();
          break;
        case 'bellmanford':
          success = await this.bellmanford();
          break;
      }
      
      this.executionTime = Date.now() - this.startTime;
      this.isRunning = false;
      
      if (!success && !this.isPaused) {
        this.$message.error('没有找到可行路径！');
      } else if (success) {
        this.$message.success(`搜索完成！探索节点数：${this.exploredNodes}，路径长度：${this.pathLength}，内存使用：${this.memoryUsage}KB`);
        this.addRecord();
      }
    },

    handlePause() {
      this.isPaused = !this.isPaused;
      if (!this.isPaused) this.startPathfinding();
    },

    reset() {
      this.isRunning = false;
      this.isPaused = false;
      this.startPos = null;
      this.endPos = null;
      this.initGrid();
      this.generateWalls();
      this.executionTime = 0;
      this.exploredNodes = 0;
      this.pathLength = 0;
      this.memoryUsage = 0;
      this.saveState();
      this.clearRecords();
    },

    handleGridSizeChange() {
      // 调整障碍物数量以保持合理比例
      const ratio = this.wallCount / (this.gridSize * this.gridSize);
      this.wallCount = Math.floor(this.gridSize * this.gridSize * ratio);
      this.wallCount = Math.min(this.maxWallCount, Math.max(this.minWallCount, this.wallCount));
      this.reset();
      this.clearRecords();
    },

    handleWallCountChange() {
      this.reset();
      this.clearRecords();
    },

    updateStatus() {
      // 统计所有被探索过的节点（包括访问节点和路径节点）
      this.exploredNodes = this.grid.flat().filter(cell => 
        cell === CELL_TYPES.VISITED || cell === CELL_TYPES.PATH
      ).length;
      // 单独统计路径长度
      this.pathLength = this.grid.flat().filter(cell => 
        cell === CELL_TYPES.PATH
      ).length;
    },

    addRecord() {
      const record = {
        id: this.records.length + 1,
        algorithm: this.selectedAlgorithm,
        exploredNodes: this.exploredNodes,
        pathLength: this.pathLength,
        executionTime: this.executionTime,
        memoryUsage: this.memoryUsage,
        timestamp: new Date().toLocaleTimeString()
      };
      this.records.unshift(record); // 新记录添加到开头
    },

    clearRecords() {
      this.records = [];
    },

    calculateMemoryUsage(dataStructures) {
      // 估算内存使用：每个节点约占用 50 字节（包括坐标、状态等信息）
      const bytesPerNode = 50;
      let totalBytes = 0;
      
      for (const structure of dataStructures) {
        if (structure instanceof Set || structure instanceof Map) {
          totalBytes += structure.size * bytesPerNode;
        } else if (Array.isArray(structure)) {
          totalBytes += structure.length * bytesPerNode;
        }
      }
      
      // 转换为 KB 并保留两位小数
      this.memoryUsage = Math.round(totalBytes / 1024 * 100) / 100;
    },

    toggleRecordsPanel() {
      this.isRecordsPanelCollapsed = !this.isRecordsPanelCollapsed;
    },
  },
  watch: {
    grid: {
      deep: true,
      handler() {
        this.updateStatus();
        requestAnimationFrame(() => this.saveState());
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.pathfinding-container {
  padding: 20px;
  background: #f0f0f0;
  min-height: calc(100vh - 40px);
  
  .container {
    display: flex;
    gap: 20px;
    justify-content: space-between;
    align-items: flex-start;
    height: calc(100vh - 80px);
  }
  
  .control-panel {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.1);
    width: 280px;
    height: 100%;
    overflow-y: auto;
    position: sticky;
    top: 20px;
    
    h2 {
      margin-top: 0;
      margin-bottom: 16px;
      font-size: 18px;
      color: #303133;
      font-weight: 600;
    }

    .settings-section {
      margin-bottom: 20px;
      padding: 16px;
      background: #f5f7fa;
      border-radius: 8px;
      border: 1px solid #e4e7ed;

      .setting-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        label {
          color: #606266;
          font-size: 14px;
        }
      }
    }
    
    .el-select {
      margin-bottom: 15px;
    }
    
    .el-button {
      margin: 4px 0;
      width: 100%;
      height: 36px;
      font-size: 14px;
    }
    
    .status {
      margin-top: 24px;
      padding: 16px;
      background: #f5f7fa;
      border-radius: 8px;
      font-size: 14px;
      border: 1px solid #e4e7ed;
      
      p {
        margin: 10px 0;
        color: #606266;
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        &:first-child {
          margin-top: 0;
        }
        
        &:last-child {
          margin-bottom: 0;
        }
        
        span {
          color: #303133;
          font-weight: 500;
        }
      }
    }
  }

  .grid-container {
    flex: 1;
    min-width: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 10px;
    overflow: auto;
  }
  
  .grid {
    display: grid;
    grid-template-columns: repeat(v-bind(gridSize), 35px);
    grid-template-rows: repeat(v-bind(gridSize), 35px);
    gap: 1px;
    background: #dcdfe6;
    padding: 1px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.1);
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      border: 1px solid #dcdfe6;
      border-radius: 8px;
      pointer-events: none;
    }
    
    .grid-row {
      display: contents;
    }
    
    .cell {
      width: 35px;
      height: 35px;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 1px solid #ebeef5;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      font-size: 16px;
      
      &:hover {
        background: #f5f7fa;
      }
      
      &.wall { 
        background: #606266;
        border-color: #606266;
      }
      
      &.start { 
        background: #67c23a;
        border-color: #67c23a;
        &::before {
          content: 'S';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-weight: bold;
          font-size: 16px;
          z-index: 1;
        }
      }
      
      &.end { 
        background: #f56c6c;
        border-color: #f56c6c;
        &::before {
          content: 'E';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-weight: bold;
          font-size: 16px;
          z-index: 1;
        }
      }
      
      &.visited { 
        animation: visitedAnimation 0.3s ease-out forwards;
        background: rgba(144, 147, 153, 0.2);
        border-color: rgba(144, 147, 153, 0.3);
      }
      
      &.path { 
        animation: pathAnimation 0.5s ease-out forwards;
        background: rgba(64, 158, 255, 0.6);
        border-color: rgba(64, 158, 255, 0.8);
      }
    }
  }

  .records-panel {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.1);
    width: 450px;
    height: 100%;
    overflow-y: auto;
    position: sticky;
    top: 20px;
    transition: all 0.3s ease;
    
    &.collapsed {
      width: 40px;
      padding: 10px;
      overflow: hidden;
      
      .records-content {
        display: none;
      }
      
      h2 {
        height: auto;
        margin: 0;
        padding: 10px 0;
        cursor: pointer;
        writing-mode: vertical-lr;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        font-size: 16px;
        letter-spacing: 2px;
        
        .toggle-icon {
          display: none;
        }
      }
    }
    
    h2 {
      margin-top: 0;
      margin-bottom: 16px;
      font-size: 18px;
      color: #303133;
      font-weight: 600;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      
      .toggle-icon {
        font-size: 16px;
        color: #909399;
        transition: transform 0.3s ease;
        
        &.collapsed {
          transform: rotate(180deg);
        }
      }
    }

    .records-content {
      height: calc(100% - 50px);
      
      .records-header {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 12px;
      }

      .records-table {
        height: calc(100% - 44px);
        
        :deep(.el-table) {
          height: 100%;
          
          th {
            background: #f5f7fa;
            color: #606266;
            font-weight: 600;
            padding: 8px 0;
          }
          
          td {
            padding: 6px 0;
          }
        }
      }

      .no-records {
        text-align: center;
        color: #909399;
        font-size: 14px;
        padding: 20px 0;
      }
    }
  }

  .algorithm-info {
    margin-top: 20px;
    padding: 16px;
    background: #f5f7fa;
    border-radius: 8px;
    border: 1px solid #e4e7ed;
    
    h3 {
      margin: 0 0 12px 0;
      font-size: 16px;
      color: #303133;
      font-weight: 600;
    }
    
    .info-content {
      h4 {
        margin: 0 0 8px 0;
        font-size: 15px;
        color: #409EFF;
      }
      
      .description {
        margin: 0 0 12px 0;
        color: #606266;
        font-size: 14px;
        line-height: 1.5;
      }
      
      .info-section {
        margin-top: 12px;
        
        h5 {
          margin: 0 0 8px 0;
          font-size: 14px;
          color: #303133;
        }
        
        ul {
          margin: 0;
          padding-left: 20px;
          
          li {
            color: #606266;
            font-size: 13px;
            margin-bottom: 4px;
            line-height: 1.4;
            
            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
    }
  }

  .button-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin: 15px 0;
    
    .el-button {
      margin: 0;
      width: 100%;
      height: 36px;
      font-size: 14px;
    }
  }
}

@keyframes visitedAnimation {
  0% {
    transform: scale(0.3);
    background: rgba(64, 158, 255, 0.8);
    border-radius: 50%;
  }
  50% {
    transform: scale(1.2);
    background: rgba(64, 158, 255, 0.5);
  }
  100% {
    transform: scale(1);
    background: rgba(144, 147, 153, 0.2);
  }
}

@keyframes pathAnimation {
  0% {
    transform: scale(0.6);
    background: rgba(64, 158, 255, 0.3);
  }
  50% {
    transform: scale(1.2);
    background: rgba(64, 158, 255, 0.5);
  }
  100% {
    transform: scale(1);
    background: rgba(64, 158, 255, 0.6);
  }
}

// 响应式布局优化
@media screen and (max-width: 1400px) {
  .pathfinding-container {
    padding: 10px;
    
    .container {
      flex-direction: column;
      height: auto;
      align-items: stretch;
    }
    
    .control-panel,
    .records-panel {
      width: 100%;
      max-width: none;
      position: static;
    }
    
    .grid-container {
      padding: 0;
      
      .grid {
        margin: 0 auto;
      }
    }
  }
}
</style> 